import tailwind from '~/tailwind.config.cjs';

export function initAnimationOnView(options = {}) {
  const defaultOptions = {
    animateOnce: true,
    speed: 500,
    threshold: 0.35,
    delay: 0,
    easing: 'ease',
    disableMutationObserver: false,
    breakpoints: {},
  };

  if (Object.keys(tailwind.theme.screens).length) {
    defaultOptions.breakpoints = tailwind.theme.screens;
  }

  const mainOptions = {
    ...defaultOptions,
    ...options,
    breakpoints: {
      ...defaultOptions.breakpoints,
      ...options.breakpoints,
    },
  };

  /* =========================
     Global CSS vars
  ========================= */
  function setGlobalProperty(name, value, isTimeUnit = false) {
    document.documentElement.style.setProperty(
        `--view-${name}`,
        isTimeUnit ? `${value}ms` : value
    );
  }

  setGlobalProperty('speed', mainOptions.speed, true);
  setGlobalProperty('easing', mainOptions.easing);

  /* =========================
     Threshold helpers
  ========================= */
  function getThreshold(element) {
    const desktop = element.dataset.viewThreshold
        ? parseFloat(element.dataset.viewThreshold)
        : mainOptions.threshold;

    const mobile = element.dataset.viewThresholdMobile
        ? parseFloat(element.dataset.viewThresholdMobile)
        : desktop;

    return window.innerWidth < 768 ? mobile : desktop;
  }

  /* =========================
     Shared IntersectionObserver
  ========================= */
  const elementThresholdMap = new WeakMap();
  const observedElements = new Set();

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const el = entry.target;
      const threshold = elementThresholdMap.get(el) ?? mainOptions.threshold;

      if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
        const delay = el.dataset.viewDelay
            ? parseInt(el.dataset.viewDelay, 10)
            : mainOptions.delay;

        if (delay > 0) {
          setTimeout(() => el.classList.add('animated'), delay);
        } else {
          el.classList.add('animated');
        }

        if (mainOptions.animateOnce) {
          observer.unobserve(el); // 🔥 КЛЮЧЕВО
          observedElements.delete(el);
        }
      } else if (!mainOptions.animateOnce) {
        el.classList.remove('animated');
      }
    });
  }, {
    threshold: [0, 0.25, 0.5, 0.75, 1],
  });

  /* =========================
     Element init
  ========================= */
  function initElement(element) {
    if (observedElements.has(element)) return;

    element.classList.add('in-view');

    if (
        element.dataset.viewDisableMobile === 'true' &&
        window.innerWidth < 768
    ) {
      element.classList.add('animated');
      return;
    }

    if (element.dataset.viewSpeed) {
      element.style.transitionDuration = `${element.dataset.viewSpeed}ms`;
    }

    if (element.dataset.viewEasing) {
      element.style.transitionTimingFunction = element.dataset.viewEasing;
    }

    elementThresholdMap.set(element, getThreshold(element));
    observedElements.add(element);
    observer.observe(element);
  }

  /* =========================
     Initial scan
  ========================= */
  document.querySelectorAll('[data-view]').forEach(initElement);

  /* =========================
     MutationObserver (SAFE)
  ========================= */
  if (!mainOptions.disableMutationObserver) {
    let mutationTimeout = null;

    const mutationObserver = new MutationObserver((mutations) => {
      clearTimeout(mutationTimeout);

      mutationTimeout = setTimeout(() => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (
                node.nodeType === 1 &&
                node.dataset &&
                'view' in node.dataset
            ) {
              initElement(node);
            }
          });
        });
      }, 50); // debounce
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
}

/* =========================
   Instant elements
========================= */
function handleInstantElements() {
  document
      .querySelectorAll('[data-view-instant="true"]')
      .forEach((el) => el.classList.add('animated'));
}

initAnimationOnView();
handleInstantElements();